import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Button, Details, Heading, Paragraph, Skeleton, Tag } from '@digdir/designsystemet-react';
import { getQuestionTextResponses, type TemplateQuestionTrends } from '../../../api/customSurveys';
import { QuestionType } from '../../../api/customSurveyTemplates';
import {
  CategoryGroup,
  choiceRows,
  formatDelta,
  formatScore,
  latestPair,
  npsCategory,
  scaleDistribution,
  scalePosition,
} from '../templateTrendsView';
import classes from '../CustomSurveyTemplateTrendsPage.module.css';

const deltaTone = (delta: number) => (delta > 0 ? 'positive' : delta < 0 ? 'negative' : 'neutral');

const scaleOf = (question: TemplateQuestionTrends) => ({
  min: question.scaleMin ?? 1,
  max: question.scaleMax ?? 5,
});

/** −100 med ekte minustegn */
const formatWhole = (value: number) => (value < 0 ? `−${Math.abs(value)}` : String(value));

/** Endringen siden forrige undersøkelse for et skala- eller NPS-spørsmål, når det finnes en forrige */
const useTrendLine = (question: TemplateQuestionTrends) => {
  const { t, i18n } = useTranslation();
  const pair = latestPair(question);
  if (!pair?.previous) return null;

  if (question.questionType === QuestionType.LIKERT_SCALE) {
    const current = pair.current.averageScore;
    const previous = pair.previous.averageScore;
    if (current === undefined || previous === undefined) return null;
    return {
      delta: current - previous,
      label: t('customSurveys.templateTrends.change', {
        delta: formatDelta(current - previous, i18n.language),
        previous: formatScore(previous, i18n.language),
      }),
    };
  }

  if (question.questionType === QuestionType.NPS_SCALE) {
    const current = pair.current.npsDistribution?.npsScore;
    const previous = pair.previous.npsDistribution?.npsScore;
    if (current === undefined || previous === undefined) return null;
    const delta = Math.round(current) - Math.round(previous);
    return {
      delta,
      label: t('customSurveys.templateTrends.change', {
        delta: delta > 0 ? `+${delta}` : formatWhole(delta),
        previous: formatWhole(Math.round(previous)),
      }),
    };
  }

  return null;
};

/** Andelen først og antallet etter, så tallene ikke leses som ett tall */
const ShareValue = ({ percent, count }: { percent: number; count: number }) => {
  const { t } = useTranslation();
  return (
    <span className={classes.barValue}>
      <strong>{t('customSurveys.templateTrends.share.percent', { percent })}</strong>
      <span className={classes.subtle}> · {t('customSurveys.templateTrends.share.count', { count })}</span>
    </span>
  );
};

const QuestionHeader = ({ question }: { question: TemplateQuestionTrends }) => {
  const { t } = useTranslation();
  const trend = useTrendLine(question);
  const { min, max } = scaleOf(question);

  return (
    <div className={classes.questionHeader}>
      <Heading level={4} data-size="2xs" className={classes.questionTitle}>
        {question.questionText}
      </Heading>
      <div className={classes.questionMeta}>
        <Tag data-size="sm" data-color="accent">
          {t(`customSurveys.templateTrends.questionTypes.${question.questionType}`, {
            min,
            max,
            defaultValue: question.questionType,
          })}
        </Tag>
        {trend && (
          <span className={classes.delta} data-tone={deltaTone(trend.delta)}>
            {trend.label}
          </span>
        )}
      </div>
    </div>
  );
};

/** Snittet for hvert skalaspørsmål i kategorien, med forrige resultat som lys strek */
const LikertComparison = ({ questions }: { questions: TemplateQuestionTrends[] }) => {
  const { t, i18n } = useTranslation();
  const scales = new Set(questions.map((question) => `${scaleOf(question).min}-${scaleOf(question).max}`));
  const { min, max } = scaleOf(questions[0]);

  return (
    <div className={classes.resultCard}>
      <Paragraph data-size="xs" className={classes.subtle}>
        {scales.size === 1
          ? t('customSurveys.templateTrends.likert.scale', { min, max })
          : t('customSurveys.templateTrends.likert.scaleMixed')}
      </Paragraph>
      <ul className={classes.likertList}>
        {questions.map((question) => {
          const pair = latestPair(question);
          const average = pair?.current.averageScore;
          if (!pair || average === undefined) {
            return (
              <li key={question.templateQuestionId} className={classes.likertRow}>
                <span className={classes.likertText}>{question.questionText}</span>
                <span aria-hidden="true" className={classes.likertTrack} />
                <span className={`${classes.likertValue} ${classes.subtle}`}>
                  <span aria-hidden="true">–</span>
                  <span className="ds-sr-only">{t('customSurveys.templateTrends.noAnswers')}</span>
                </span>
              </li>
            );
          }
          const scale = scaleOf(question);
          const previous = pair.previous?.averageScore;
          const position = scalePosition(average, scale.min, scale.max);
          const delta = previous !== undefined ? average - previous : undefined;

          return (
            <li key={question.templateQuestionId} className={classes.likertRow}>
              <span className={classes.likertText}>{question.questionText}</span>
              <span aria-hidden="true" className={classes.likertTrack}>
                <span className={classes.likertFill} data-low={position < 0.5} style={{ width: `${position * 100}%` }} />
                {previous !== undefined && (
                  <span
                    className={classes.previousMarker}
                    style={{ left: `${scalePosition(previous, scale.min, scale.max) * 100}%` }}
                  />
                )}
              </span>
              <span className={classes.likertValue}>{formatScore(average, i18n.language)}</span>
              {delta !== undefined && (
                <span className={classes.delta} data-tone={deltaTone(delta)}>
                  <span className="ds-sr-only">{t('customSurveys.templateTrends.changeSincePrevious')}</span>
                  {formatDelta(delta, i18n.language)}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

/** Spørsmål uten svar vises også, så ingen spørsmål forsvinner */
const NoAnswersCard = ({ question }: { question: TemplateQuestionTrends }) => {
  const { t } = useTranslation();
  return (
    <li className={classes.resultCard}>
      <QuestionHeader question={question} />
      <Paragraph data-size="sm" className={classes.subtle}>
        {t('customSurveys.templateTrends.noAnswers')}
      </Paragraph>
    </li>
  );
};

/** Ett skalaspørsmål: snitt, median og hvor mange som svarte hver verdi */
const LikertCard = ({ question }: { question: TemplateQuestionTrends }) => {
  const { t, i18n } = useTranslation();
  const point = latestPair(question)?.current;
  if (!point || point.averageScore === undefined) return null;
  const { min, max } = scaleOf(question);
  const columns = scaleDistribution(point, min, max);
  const highest = Math.max(1, ...columns.map((column) => column.count));

  return (
    <li className={classes.resultCard}>
      <QuestionHeader question={question} />
      <dl className={classes.likertStats}>
        <div>
          <dt>{t('customSurveys.templateTrends.likert.average')}</dt>
          <dd>
            <span className={classes.likertStatValue}>{formatScore(point.averageScore, i18n.language)}</span>{' '}
            {t('customSurveys.templateTrends.likert.of', { max })}
          </dd>
        </div>
        {point.medianScore !== undefined && point.medianScore !== null && (
          <div>
            <dt>{t('customSurveys.templateTrends.likert.median')}</dt>
            <dd>
              <span className={classes.likertStatValue}>{formatScore(point.medianScore, i18n.language, 1)}</span>{' '}
              {t('customSurveys.templateTrends.likert.of', { max })}
            </dd>
          </div>
        )}
      </dl>
      {point.optionDistribution && Object.keys(point.optionDistribution).length > 0 && (
        <div className={classes.chart}>
          <div
            role="img"
            aria-label={t('customSurveys.templateTrends.likert.distribution', {
              values: columns
                .map(({ value, count }) => t('customSurveys.templateTrends.likert.distributionValue', { count, value }))
                .join(', '),
            })}
            className={classes.histogram}
            style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}
          >
            {columns.map(({ value, count }) => (
              <span key={value} className={classes.histogramColumn}>
                <span className={classes.histogramCount}>{count > 0 ? count : ''}</span>
                <span
                  className={classes.histogramBar}
                  data-top={count === highest}
                  style={{ height: `${(count / highest) * 85}%` }}
                />
              </span>
            ))}
          </div>
          <div
            aria-hidden="true"
            className={classes.histogramAxis}
            style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}
          >
            {columns.map(({ value }) => (
              <span key={value}>{value}</span>
            ))}
          </div>
        </div>
      )}
    </li>
  );
};

const NpsCard = ({ question }: { question: TemplateQuestionTrends }) => {
  const { t } = useTranslation();
  const distribution = latestPair(question)?.current.npsDistribution;
  if (!distribution) return null;

  const score = Math.round(distribution.npsScore);
  const category = npsCategory(score);
  const rows = [
    { key: 'promoters', count: distribution.promoters, percent: distribution.promoterPercentage, tone: 'positive' },
    { key: 'passives', count: distribution.passives, percent: distribution.passivePercentage, tone: 'neutral' },
    { key: 'detractors', count: distribution.detractors, percent: distribution.detractorPercentage, tone: 'negative' },
  ];

  return (
    <li className={classes.resultCard}>
      <QuestionHeader question={question} />
      <div className={classes.npsBody}>
        <div className={classes.npsScore}>
          <span className={classes.subtle}>{t('customSurveys.templateTrends.nps.index')}</span>
          <span className={classes.npsValue}>{formatWhole(score)}</span>
          <div>
            <Tag data-size="sm" data-color={category === 'poor' ? 'danger' : category === 'good' ? 'info' : 'success'}>
              {t(`customSurveys.templateTrends.nps.categories.${category}`)}
            </Tag>
          </div>
        </div>
        <ul className={classes.barList}>
          {rows.map((row) => (
            <li key={row.key} className={classes.barRow}>
              <span>{t(`customSurveys.templateTrends.nps.${row.key}`)}</span>
              <span aria-hidden="true" className={classes.barTrack}>
                <span className={classes.barFill} data-tone={row.tone} style={{ width: `${row.percent}%` }} />
              </span>
              <ShareValue percent={Math.round(row.percent)} count={row.count} />
            </li>
          ))}
        </ul>
      </div>
      <Paragraph data-size="xs" className={classes.subtle}>
        {t('customSurveys.templateTrends.nps.explanation')}
      </Paragraph>
    </li>
  );
};

const ChoiceCard = ({ question }: { question: TemplateQuestionTrends }) => {
  const { t } = useTranslation();
  const pair = latestPair(question);
  if (!pair) return null;
  const rows = choiceRows(question, pair.current, {
    yes: t('customSurveys.templateTrends.choice.yes'),
    no: t('customSurveys.templateTrends.choice.no'),
  });

  return (
    <li className={classes.resultCard}>
      <QuestionHeader question={question} />
      <ul className={classes.choiceList}>
        {rows.map((row) => (
          <li key={row.key} className={classes.choiceRow}>
            <span>{row.label}</span>
            <span aria-hidden="true" className={classes.choiceTrack}>
              <span
                className={classes.barFill}
                data-tone={row.isTop ? 'accent' : 'other'}
                style={{ width: `${row.percent}%` }}
              />
            </span>
            <ShareValue percent={row.percent} count={row.count} />
          </li>
        ))}
      </ul>
      {question.questionType === QuestionType.MULTIPLE_SELECT && (
        <Paragraph data-size="xs" className={classes.subtle}>
          {t('customSurveys.templateTrends.choice.multipleNote')}
        </Paragraph>
      )}
    </li>
  );
};

const TextCard = ({ question, surveyId }: { question: TemplateQuestionTrends; surveyId?: number }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const point = latestPair(question)?.current;
  const inline = point?.textResponses;
  // På én undersøkelse hentes tekstsvarene først når de åpnes
  const { data, isLoading, isError } = useQuery({
    queryKey: ['customSurveyTextResponses', surveyId, question.templateQuestionId],
    queryFn: () => getQuestionTextResponses(surveyId!, question.templateQuestionId),
    enabled: open && !inline && surveyId !== undefined,
  });
  const responses = inline ?? data?.textResponses.map((response) => response.textValue) ?? [];
  const count = inline ? inline.length : (point?.responseCount ?? 0);

  return (
    <li className={classes.resultCard}>
      <QuestionHeader question={question} />
      {count === 0 ? (
        <Paragraph data-size="sm" className={classes.subtle}>
          {t('customSurveys.templateTrends.text.none')}
        </Paragraph>
      ) : (
        <Details open={open} onToggle={() => setOpen((isOpen) => !isOpen)}>
          <Details.Summary>{t('customSurveys.templateTrends.text.show', { count })}</Details.Summary>
          <Details.Content>
            {isLoading ? (
              <Skeleton variant="text" width="100%" />
            ) : isError ? (
              <Paragraph data-size="sm">{t('customSurveys.templateTrends.text.error')}</Paragraph>
            ) : (
              <ul className={classes.textList}>
                {responses.map((response, index) => (
                  <li key={index}>{response}</li>
                ))}
              </ul>
            )}
          </Details.Content>
        </Details>
      )}
    </li>
  );
};

const hasAnswers = (question: TemplateQuestionTrends) => {
  const point = latestPair(question)?.current;
  if (!point || point.responseCount === 0) return false;
  switch (question.questionType) {
    case QuestionType.LIKERT_SCALE:
      return point.averageScore !== undefined && point.averageScore !== null;
    case QuestionType.NPS_SCALE:
      return Boolean(point.npsDistribution);
    default:
      return true;
  }
};

const QuestionCard = ({ question, surveyId }: { question: TemplateQuestionTrends; surveyId?: number }) => {
  if (!hasAnswers(question)) return <NoAnswersCard question={question} />;
  switch (question.questionType) {
    case QuestionType.LIKERT_SCALE:
      return <LikertCard question={question} />;
    case QuestionType.NPS_SCALE:
      return <NpsCard question={question} />;
    case QuestionType.SHORT_TEXT:
    case QuestionType.LONG_TEXT:
      return <TextCard question={question} surveyId={surveyId} />;
    default:
      return <ChoiceCard question={question} />;
  }
};

type CategoryResultsProps = {
  group: CategoryGroup;
  /** Undersøkelsen som vises, når siden viser én undersøkelse */
  surveyId?: number;
};

export const CategoryResults = ({ group, surveyId }: CategoryResultsProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const headingId = `category-${group.category ?? 'other'}`.replace(/\s+/g, '-');
  // Flere skalaspørsmål sammenlignes i ett kort, og spørsmålene kan foldes ut under
  const hasComparison = group.likert.length >= 2;
  const showQuestions = !hasComparison || expanded;

  return (
    <section aria-labelledby={headingId} className={classes.category}>
      <div className={classes.categoryHeader}>
        <div className={classes.categoryTitle}>
          {group.category && (
            <span className={classes.categoryEyebrow}>{t('customSurveys.templateTrends.category')}</span>
          )}
          <Heading level={3} data-size="xs" id={headingId}>
            {group.category ?? t('customSurveys.templateTrends.uncategorized')}
          </Heading>
        </div>
        <span className={classes.subtle}>
          {group.questionCount > 1
            ? t('customSurveys.templateTrends.categoryMeta', {
                questions: group.questionCount,
                count: group.responseCount,
              })
            : t('customSurveys.templateTrends.responses', { count: group.responseCount })}
        </span>
      </div>

      {hasComparison && <LikertComparison questions={group.likert} />}

      {hasComparison && (
        <div>
          <Button
            variant="tertiary"
            data-size="sm"
            aria-expanded={expanded}
            onClick={() => setExpanded((isExpanded) => !isExpanded)}
          >
            {expanded
              ? t('customSurveys.templateTrends.questions.hide')
              : t('customSurveys.templateTrends.questions.showAll', { count: group.questions.length })}
          </Button>
        </div>
      )}

      {showQuestions && group.questions.length > 0 && (
        <ol className={classes.questionGrid} data-single={group.questions.length === 1}>
          {group.questions.map((question) => (
            <QuestionCard key={question.templateQuestionId} question={question} surveyId={surveyId} />
          ))}
        </ol>
      )}
    </section>
  );
};
